import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LOST_REASON_MIN, type Opportunity, type PendingLostReason } from "@salesboard/contracts";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Field, Textarea } from "@/components/ui/input";
import { useT } from "@/i18n";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useSession } from "@/features/auth/useSession";

/**
 * Asks for the reasons nobody gave.
 *
 * A lost opportunity must say why it was lost, and the closure dialog enforces that from now
 * on — but two kinds of record slip past it: everything closed before the rule existed, and
 * anything closed in a session that ended before the answer was typed. So the question is asked
 * again on arrival, once per record, until there are none left.
 *
 * **The dialog has no way out.** No close button, no backdrop dismissal, no Escape: those are
 * the three exits Radix gives a dialog by default and all three are refused here, because an
 * unexplained loss is exactly what this exists to prevent and a dialog one can wave away
 * prevents nothing. The only path through it is an answer — which is also why it asks for the
 * *reader's own* records only (the API scopes it to the session), so nobody can be held at the
 * door by somebody else's paperwork.
 *
 * One at a time, oldest closure first, each naming its record and its client: a queue of four
 * dialogs at once would be answered with four copies of the same word.
 */
export function LostReasonSweep(): React.JSX.Element | null {
  const t = useT();
  const session = useSession();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const pending = useQuery({
    queryKey: ["opportunities", "lost-reasons", "pending"],
    queryFn: () => api.get<PendingLostReason[]>("/opportunities/lost-reasons/pending"),
    // Only for a signed-in reader, and not while the session is still resolving.
    enabled: Boolean(session.data),
    // Asked once per arrival rather than kept fresh: this is a chore list, and re-fetching it
    // while somebody is typing into it would be an interruption in the middle of an answer.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const save = useMutation({
    mutationFn: ({ id, version, lostReason }: { id: string; version: number; lostReason: string }) =>
      api.patch<Opportunity>(`/opportunities/${id}`, { lostReason, version }),
    onSuccess: async () => {
      setDraft("");
      // Re-read the queue rather than splicing the answered record out of it: the server is
      // the authority on what is still missing, and somebody else may have answered one too.
      await queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });

  const record = pending.data?.[0];
  if (!record) return null;

  const tooShort = draft.trim().length < LOST_REASON_MIN;
  const remaining = (pending.data?.length ?? 1) - 1;

  return (
    <Dialog open>
      <DialogContent
        // The three exits, all refused. `showClose` is this shell's own prop; the other
        // two are Radix's defaults, and both have to be prevented explicitly.
        showClose={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="sm:max-w-lg"
      >
        <DialogTitle>{t("opportunity.lostReasonFor", record.name)}</DialogTitle>
        <DialogDescription>{t("opportunity.lostReasonRequired")}</DialogDescription>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (tooShort) return;
            save.mutate({ id: record.id, version: record.version, lostReason: draft.trim() });
          }}
          className="space-y-4"
        >
          {/* Which record this is about — a dialog that cannot be dismissed must at least be
              unambiguous about what it is asking for. */}
          <dl className="bg-muted/40 grid gap-1 rounded-md p-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("column.client")}</dt>
              <dd className="font-medium">{record.clientName}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("opportunity.stage")}</dt>
              <dd className="font-medium">{record.stageName}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t("opportunity.actualClose")}</dt>
              <dd className="font-medium">{formatDate(record.actualCloseDate)}</dd>
            </div>
          </dl>

          <Field
            label={t("opportunity.lostReason")}
            htmlFor="sweep-lost-reason"
            hint={t("opportunity.lostReasonTooShort", LOST_REASON_MIN)}
            error={save.error ? t("opportunity.lostReasonTooShort", LOST_REASON_MIN) : undefined}
          >
            <Textarea
              id="sweep-lost-reason"
              value={draft}
              rows={3}
              placeholder={t("opportunity.lostReasonPlaceholder")}
              onChange={(event) => setDraft(event.target.value)}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </Field>

          <div className="flex items-center justify-between gap-4">
            {/* How many more, so an answer feels like progress rather than a loop. */}
            <span className="text-muted-foreground text-xs">
              {remaining > 0 ? t("opportunity.lostReasonRemaining", remaining) : ""}
            </span>
            <Button type="submit" disabled={tooShort || save.isPending}>
              {save.isPending ? t("common.saving") : t("common.done")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
